export interface AutomotiveReview {
  name: string;
  initials: string;
  color: string;
  badges: string[];
  rating: number;
  date: string;
  text: string;
  helpful: number;
}

export const automotiveReviews: Record<string, AutomotiveReview[]> = {
  kkday: [
    {
      name: 'Daniel W.',
      initials: 'DW',
      color: 'bg-orange-500',
      badges: ['Verified'],
      rating: 5,
      date: 'Feb 2026',
      text: "Used KKday to book a self-drive itinerary through Japan's Hokuriku region and it was flawless. The vehicle rental was seamlessly integrated with our activity bookings, and the offline itinerary feature saved us when we had no signal in rural areas. Customer support was quick and helpful when we needed to adjust our pickup time. Can't recommend enough for road trip planning in Asia!",
      helpful: 134,
    },
    {
      name: 'Sophie M.',
      initials: 'SM',
      color: 'bg-amber-500',
      badges: ['Verified', 'Frequent Traveler'],
      rating: 5,
      date: 'Jan 2026',
      text: 'KKday has completely changed how I plan Asian road trips. The combination of vehicle rentals, highway pass purchases, and activity bookings in one platform saves so much coordination time. I booked a full Taiwan circumnavigation road trip through KKday and everything worked perfectly. Prices are genuinely competitive and the operator reviews are reliable.',
      helpful: 98,
    },
    {
      name: 'Marcus T.',
      initials: 'MT',
      color: 'bg-red-500',
      badges: ['Verified'],
      rating: 4,
      date: 'Dec 2025',
      text: 'Excellent platform with a massive selection of automotive experiences including track days and race events I never would have found otherwise. Booked a GT car driving experience in Taipei that was absolutely incredible. Only minor issue was one operator had changed their meeting point without updating the platform, but KKday support resolved it quickly.',
      helpful: 76,
    },
  ],
  maxpeedingrods: [
    {
      name: 'Chris R.',
      initials: 'CR',
      color: 'bg-red-600',
      badges: ['Verified', 'Car Enthusiast'],
      rating: 5,
      date: 'Feb 2026',
      text: "Installed Maxpeedingrods coilovers on my E46 BMW and the handling transformation is remarkable. The ride quality is exactly where I wanted it — firm but not punishing — and the adjustability means I can dial it in for track days vs daily driving. At less than half the price of comparable KW coilovers with nearly identical performance, this is exceptional value. 10/10 would buy again.",
      helpful: 189,
    },
    {
      name: 'Ryan K.',
      initials: 'RK',
      color: 'bg-rose-600',
      badges: ['Verified', 'Track Day Driver'],
      rating: 4,
      date: 'Jan 2026',
      text: "Running Maxpeedingrods coilovers for 8 months now including 6 track days and zero issues. The build quality is genuinely impressive for the price point — threads are clean, adjustment is smooth, and the shock damping feels consistent. I was skeptical of a budget brand for track use but these have absolutely earned my trust.",
      helpful: 143,
    },
    {
      name: 'Jake L.',
      initials: 'JL',
      color: 'bg-red-500',
      badges: ['Verified'],
      rating: 5,
      date: 'Nov 2025',
      text: "Got the turbo kit for my Subaru WRX and installation was straightforward with their detailed guide. The power gain is exactly as advertised and the build quality looks and feels premium. Shipping from their US warehouse arrived in 3 days. Maxpeedingrods has seriously disrupted the overpriced performance parts market and I'm here for it.",
      helpful: 167,
    },
  ],
  kemimoto: [
    {
      name: 'Tyler B.',
      initials: 'TB',
      color: 'bg-slate-700',
      badges: ['Verified', 'UTV Rider'],
      rating: 5,
      date: 'Feb 2026',
      text: 'Equipped my Polaris RZR with Kemimoto windshield, mirrors, and phone mount before a week-long Utah desert trip. All three performed perfectly in brutal heat and rough terrain. The windshield fit was exact, installation took 20 minutes, and the clarity is excellent. Kemimoto has earned permanent space in my accessories budget.',
      helpful: 112,
    },
    {
      name: 'Amanda C.',
      initials: 'AC',
      color: 'bg-gray-700',
      badges: ['Verified', 'ATV Rider'],
      rating: 4,
      date: 'Jan 2026',
      text: "Really impressed with Kemimoto's quality at this price point. Bought protective covers and soft doors for my Can-Am Maverick and both are superb. The materials are clearly chosen for durability, not just appearance. Shipping was fast and packaging was excellent. One minor complaint: the installation instructions could be a bit clearer, but nothing a quick YouTube search can't solve.",
      helpful: 88,
    },
    {
      name: 'Derek H.',
      initials: 'DH',
      color: 'bg-slate-600',
      badges: ['Verified'],
      rating: 5,
      date: 'Dec 2025',
      text: "Have tried several ATV accessory brands over the years and Kemimoto consistently delivers the best balance of quality and price. Their roof system for my Yamaha Viking fit perfectly and has survived three seasons of serious off-road use without any issues. Customer service is also excellent — they sent replacement hardware without any hassle when one bolt was missing.",
      helpful: 94,
    },
  ],
  fapomoto: [
    {
      name: 'Luke P.',
      initials: 'LP',
      color: 'bg-zinc-700',
      badges: ['Verified', 'Moto Commuter'],
      rating: 5,
      date: 'Feb 2026',
      text: "Fapomoto's phone mount has been on my adventure bike for 15,000 miles of commuting and touring. The vibration dampening is exceptional — zero image blur even on rough roads — and the weatherproofing has survived heavy rain without any issues. This is simply the best motorcycle phone mount I've ever used and I've tried maybe 8 different ones.",
      helpful: 156,
    },
    {
      name: 'Sarah J.',
      initials: 'SJ',
      color: 'bg-slate-700',
      badges: ['Verified'],
      rating: 4,
      date: 'Jan 2026',
      text: "Got the handlebar bag for touring and it's excellent — well made, waterproof, and holds a surprising amount. The attachment system is secure and doesn't rattle. My only minor gripe is the zipper could be a bit smoother. But for the price and overall quality, this is a steal compared to branded alternatives from bigger names.",
      helpful: 78,
    },
    {
      name: 'Carlos M.',
      initials: 'CM',
      color: 'bg-gray-700',
      badges: ['Verified', 'Sport Rider'],
      rating: 5,
      date: 'Nov 2025',
      text: "Fapomoto windshield for my Kawasaki Z900RS is perfect. Excellent wind protection at highway speeds, fits precisely without any modification, and the acrylic quality is excellent with no distortion. Installation was literally 10 minutes. These guys clearly understand what riders actually need. Will definitely buy from Fapomoto again.",
      helpful: 102,
    },
  ],
  carpuride: [
    {
      name: 'Michael S.',
      initials: 'MS',
      color: 'bg-sky-600',
      badges: ['Verified'],
      rating: 5,
      date: 'Feb 2026',
      text: "The Carpuride W901 CarPlay display completely transformed my 2008 pickup truck. My wife thinks I got a new car — the interface looks better than many factory systems costing thousands more. Wireless CarPlay connects in seconds, the touchscreen response is fast and accurate, and the backup camera quality is excellent. Best $150 I've ever spent on a vehicle.",
      helpful: 243,
    },
    {
      name: 'Jennifer K.',
      initials: 'JK',
      color: 'bg-cyan-600',
      badges: ['Verified', 'Long-Distance Driver'],
      rating: 5,
      date: 'Jan 2026',
      text: "I was skeptical about a portable CarPlay display but Carpuride totally won me over. The screen brightness is genuinely impressive in direct sunlight — way better than my phone mounted on the dash. Apple Maps and Waze work flawlessly through it. The installation kit included everything I needed. For anyone with an older vehicle who wants modern connected car features, this is an absolute no-brainer.",
      helpful: 198,
    },
    {
      name: 'Tom R.',
      initials: 'TR',
      color: 'bg-blue-600',
      badges: ['Verified'],
      rating: 4,
      date: 'Dec 2025',
      text: "Really solid product. Wireless CarPlay works great about 95% of the time — occasionally needs a reconnect, but that's minor. The display quality is sharp and the processor keeps up well with CarPlay demands. I added the backup camera and that integration is seamless. For the price vs factory CarPlay retrofit options, Carpuride wins easily.",
      helpful: 134,
    },
  ],
  prismbeam: [
    {
      name: 'Kevin W.',
      initials: 'KW',
      color: 'bg-amber-600',
      badges: ['Verified'],
      rating: 5,
      date: 'Feb 2026',
      text: "The difference Prismbeam headlights made to my night driving visibility is genuinely startling. My old halogens were embarrassingly dim by comparison. The beam pattern is excellent — excellent far-field reach without blinding oncoming drivers. Easy plug-and-play installation on my Toyota Camry with zero error codes. These are staying on my car permanently.",
      helpful: 178,
    },
    {
      name: 'Rachel L.',
      initials: 'RL',
      color: 'bg-yellow-600',
      badges: ['Verified', 'Night Driver'],
      rating: 5,
      date: 'Jan 2026',
      text: "I drive a lot of unlit country roads at night and upgrading to Prismbeam was a safety game-changer. The improvement in road illumination is dramatic. The white color temperature looks modern and premium. Installation took 5 minutes — just swap and go. I've had zero issues over 6 months of daily use. Highly recommend to anyone still running halogens.",
      helpful: 154,
    },
    {
      name: 'Brian H.',
      initials: 'BH',
      color: 'bg-orange-600',
      badges: ['Verified'],
      rating: 4,
      date: 'Nov 2025',
      text: 'Excellent LED bulbs that deliver exactly what they promise in terms of brightness. On my Ford F-150 with the projector housing, the beam pattern is perfect. I did need the CANBUS adapter to prevent the bulb warning light, but Prismbeam\'s website mentions this upfront and the adapter is cheap. Build quality feels premium and I expect these to last years.',
      helpful: 119,
    },
  ],
  'automobile-magazine': [
    {
      name: 'Paul T.',
      initials: 'PT',
      color: 'bg-gray-700',
      badges: ['Verified', 'Auto Journalist'],
      rating: 5,
      date: 'Feb 2026',
      text: "Automobile Magazine remains one of the few automotive publications that actually drives me to think differently about cars. Their long-term tests are invaluable — there's so much you learn about a vehicle after 40,000 miles that no other format reveals. The writing quality is also consistently excellent. Real journalism about cars, not just press release regurgitation.",
      helpful: 112,
    },
    {
      name: 'Sandra M.',
      initials: 'SM',
      color: 'bg-slate-600',
      badges: ['Verified'],
      rating: 4,
      date: 'Jan 2026',
      text: "Used Automobile's comparison tests extensively when researching my last car purchase and they were genuinely helpful. The editorial team clearly has strong opinions backed by real experience, which I respect. The website is clean and easy to navigate. I'd give 5 stars if the digital experience was polished as the print edition.",
      helpful: 87,
    },
    {
      name: 'James F.',
      initials: 'JF',
      color: 'bg-gray-600',
      badges: ['Verified', 'Subscriber'],
      rating: 5,
      date: 'Dec 2025',
      text: 'Been reading Automobile for 20 years. Their coverage of performance cars and driving dynamics is unmatched in US automotive journalism. The writing is smart, the photography is stunning, and the perspective is genuinely enthusiast-focused rather than chase-the-ad-dollars journalism. One of the last great car magazines.',
      helpful: 98,
    },
  ],
  freightwave: [
    {
      name: 'Alex R.',
      initials: 'AR',
      color: 'bg-blue-700',
      badges: ['Verified', 'Logistics VP'],
      rating: 5,
      date: 'Feb 2026',
      text: 'FreightWaves SONAR has become absolutely essential to our logistics operations. The ability to see spot rate trends, capacity tightness, and tender rejection rates in near real-time gives us genuine competitive advantage when negotiating contracts and managing our carrier network. The editorial coverage is also excellent — the best freight journalism out there. Worth every dollar of the subscription.',
      helpful: 167,
    },
    {
      name: 'Michelle C.',
      initials: 'MC',
      color: 'bg-indigo-700',
      badges: ['Verified', 'Freight Broker'],
      rating: 5,
      date: 'Jan 2026',
      text: "FreightWaves transformed how I understand the freight market. Before SONAR, I was reacting to market changes after they happened. Now I can see them coming. The free news content alone is worth bookmarking — it's the most comprehensive freight coverage available anywhere online.",
      helpful: 143,
    },
    {
      name: 'Robert K.',
      initials: 'RK',
      color: 'bg-blue-600',
      badges: ['Verified'],
      rating: 4,
      date: 'Nov 2025',
      text: "Excellent freight intelligence platform. The news coverage is genuinely class-leading and the podcast content is excellent for my commute. SONAR is incredibly powerful but the learning curve is real — budget time to get up to speed. Once you do, though, you'll wonder how you managed without it. A must-have for anyone who takes freight markets seriously.",
      helpful: 112,
    },
  ],
  'transport-topics': [
    {
      name: 'David W.',
      initials: 'DW',
      color: 'bg-slate-600',
      badges: ['Verified', 'Fleet Manager'],
      rating: 5,
      date: 'Feb 2026',
      text: "Transport Topics is my daily read. It's the only publication that consistently covers regulatory changes affecting our fleet operations with the depth and accuracy we need. The Top 100 Carrier list is an industry institution. For fleet executives who need to stay current on what's shaping commercial transportation, TT is simply essential.",
      helpful: 98,
    },
    {
      name: 'Sandra H.',
      initials: 'SH',
      color: 'bg-gray-600',
      badges: ['Verified', 'Trucking Executive'],
      rating: 4,
      date: 'Jan 2026',
      text: "Solid industry resource with excellent coverage of the big stories impacting commercial transportation. The EV truck coverage has been particularly good as the industry navigates electrification. Would love to see more in-depth financial analysis pieces, but overall this remains the most credible voice in commercial trucking journalism.",
      helpful: 76,
    },
    {
      name: 'Mike T.',
      initials: 'MT',
      color: 'bg-slate-700',
      badges: ['Verified'],
      rating: 5,
      date: 'Dec 2025',
      text: "Transport Topics rankings are trusted currency in our industry. When I need to brief our board on competitive landscape, TT's carrier and logistics rankings are what I pull. The daily newsletter keeps me current without drowning in irrelevant content. A genuinely useful publication from a team that understands commercial transportation.",
      helpful: 87,
    },
  ],
  joc: [
    {
      name: 'Lisa P.',
      initials: 'LP',
      color: 'bg-cyan-700',
      badges: ['Verified', 'Supply Chain Director'],
      rating: 5,
      date: 'Feb 2026',
      text: "JOC Port Tracker has saved our company enormous cost and headache by giving us early visibility into port congestion before it impacts our shipments. When everyone was scrambling during the LA/Long Beach backlog, we had already rerouted through alternative ports weeks earlier because of JOC's data. That alone justified years of subscription cost.",
      helpful: 189,
    },
    {
      name: 'Thomas K.',
      initials: 'TK',
      color: 'bg-teal-700',
      badges: ['Verified', 'Freight Forwarder'],
      rating: 4,
      date: 'Jan 2026',
      text: "JOC editorial is genuinely the best ocean freight journalism available — knowledgeable reporters who actually understand shipping, not generalist journalists parachuting in. The market intelligence products are powerful but expensive for smaller forwarders. The free content tier still delivers a lot of value for daily market awareness.",
      helpful: 134,
    },
    {
      name: 'Karen L.',
      initials: 'KL',
      color: 'bg-cyan-600',
      badges: ['Verified', 'Logistics Analyst'],
      rating: 5,
      date: 'Nov 2025',
      text: "JOC is my first read every morning for ocean market intelligence. The depth of their trade lane analysis and carrier strategy coverage is unmatched. For anyone involved in international sourcing or ocean freight procurement, understanding what JOC covers is not optional — it's fundamental to doing your job well.",
      helpful: 112,
    },
  ],
  cartrade: [
    {
      name: 'Priya S.',
      initials: 'PS',
      color: 'bg-emerald-600',
      badges: ['Verified'],
      rating: 5,
      date: 'Feb 2026',
      text: "CarTrade made finding my used car so much easier than I expected. The valuation tool gave me a realistic price expectation before I started negotiating, and the inspection report on the car I bought gave me confidence it hadn't been in any major accidents. The loan pre-approval through the platform was also seamless. Would definitely use again.",
      helpful: 143,
    },
    {
      name: 'Rahul M.',
      initials: 'RM',
      color: 'bg-teal-600',
      badges: ['Verified', 'Seller'],
      rating: 4,
      date: 'Jan 2026',
      text: "Sold my Maruti Swift through CarTrade and got a better price than any other channel I tried. The promoted listing got good visibility quickly. The process was straightforward and CarTrade's support was helpful when I had documentation questions. Only small complaint: the inquiry quality varies and you have to filter out some lowball offers.",
      helpful: 98,
    },
    {
      name: 'Amit K.',
      initials: 'AK',
      color: 'bg-green-600',
      badges: ['Verified'],
      rating: 5,
      date: 'Dec 2025',
      text: "Best car buying experience I've had. Found a certified pre-owned Hyundai Creta at a fair price using CarTrade's market comparison tool. The platform clearly showed me how the asking price compared to similar vehicles. Financing was handled entirely through CarTrade's partner network. The whole process from search to delivery took less than a week.",
      helpful: 167,
    },
  ],
  'jiji-africa': [
    {
      name: 'Emmanuel O.',
      initials: 'EO',
      color: 'bg-orange-600',
      badges: ['Verified', 'Lagos'],
      rating: 5,
      date: 'Feb 2026',
      text: "Jiji has completely changed how I buy and sell vehicles in Nigeria. The platform is massive — thousands of genuine listings for every type of vehicle. I've bought two cars and sold one through Jiji and all three transactions went smoothly. The direct chat feature makes it easy to quickly qualify serious buyers. Essential platform for anyone in the Nigerian car market.",
      helpful: 178,
    },
    {
      name: 'Kwame A.',
      initials: 'KA',
      color: 'bg-red-600',
      badges: ['Verified', 'Ghana'],
      rating: 4,
      date: 'Jan 2026',
      text: "Jiji Ghana is by far the best place to find vehicles in the country. The selection is genuinely impressive and prices are competitive because of the large seller base. The verified badge system helps identify trustworthy sellers. Just do your due diligence before any transaction — view the vehicle in person and check documentation carefully. Overall a great platform.",
      helpful: 134,
    },
    {
      name: 'Nia W.',
      initials: 'NW',
      color: 'bg-amber-600',
      badges: ['Verified', 'Kenya'],
      rating: 5,
      date: 'Nov 2025',
      text: "In Nairobi, Jiji is the go-to for used cars — nothing else comes close for selection or price transparency. The mobile app is excellent and works well even on slower connections, which is important in Kenya. I found my Toyota Premio at exactly the right price after comparing dozens of listings. Jiji has genuinely modernized the used car market in East Africa.",
      helpful: 112,
    },
  ],
};
